/**
 * Maps Clinical Intake "template" checkbox field keys to standard text in
 * `templateLanguageUpdatedBase` (by title key). When `baseTitle` is null, the LLM
 * uses only the section label + clinical intake (`allowEmptyBase`).
 * `focusNote` narrows a shared base (e.g. 11.3) to a subsection.
 */
export const TEMPLATE_INTAKE_FIELD_META = {
  regulatory_ethical_considerations: {
    baseTitle: '11.1 Regulatory and Ethical Considerations',
    sectionLabel: '11.1 Regulatory and Ethical Considerations',
    focusNote:
      'Tailor to region(s) and regulatory framework(s) in the clinical intake (e.g., US FDA/IND vs EU CTR 536/2014). Keep the core common-text bullets; include only the regulations that apply to the current project.',
  },
  investigator_responsibilities: {
    baseTitle: '11.2.1 Investigators Responsibilities',
    sectionLabel: '11.2.1 Investigators Responsibilities',
  },
  sponsor_responsibilities: {
    baseTitle: '11.2.2 Sponsor Responsibilities',
    sectionLabel: '11.2.2 Sponsor Responsibilities',
  },
  informed_consent_rescreening: {
    baseTitle: '11.3 Informed Consent Process',
    sectionLabel: '11.3.1 Informed Consent for Rescreening',
    focusNote:
      'Emphasize only rescreening / re-consent rules; omit unrelated informed consent topics where possible.',
  },
  informed_consent_remaining_samples: {
    baseTitle: '11.3 Informed Consent Process',
    sectionLabel: '11.3.2 Informed Consent for Use of Remaining Samples in Exploratory Research',
    focusNote:
      'Emphasize only optional exploratory use of remaining samples; omit unrelated informed consent topics where possible.',
  },
  committees: {
    baseTitle: '11.4 Committees',
    sectionLabel: '11.4 Committees',
    focusNote:
      'If clinical intake does not identify any oversight committee (DMC, DSMB, IDMC, safety review, dose escalation, steering, etc.), output exactly: Not applicable. Do not use example-text committee language.',
  },
  insurance_indemnity: {
    baseTitle: '11.5 Insurance and Indemnity',
    sectionLabel: '11.5 Insurance and Indemnity',
  },
  risk_based_quality_management: {
    baseTitle: '11.6 Risk-Based Quality Management',
    sectionLabel: '11.6 Risk-Based Quality Management',
  },
  data_governance: {
    baseTitle: '11.7 Data Governance',
    sectionLabel: '11.7 Data Governance',
  },
  data_protection: {
    baseTitle: '11.8 Data Protection',
    sectionLabel: '11.8 Data Protection',
  },
  source_data: {
    baseTitle: '11.9 Source Reports',
    sectionLabel: '11.9 Source Data / Source Reports',
  },
  protocol_deviations: {
    baseTitle: '11.10 Protocol Deviations',
    sectionLabel: '11.10 Protocol Deviations',
  },
  early_site_closure: {
    baseTitle: '11.11 Early Site Closure',
    sectionLabel: '11.11 Early Site Closure',
  },
  data_dissemination: {
    baseTitle: '11.12 Data Dissemination',
    sectionLabel: '11.12 Data Dissemination',
  },
  definitions_childbearing_potential: {
    baseTitle: '5.4.1 Definitions Related to Childbearing Potential',
    sectionLabel: '5.4.1 Definitions Related to Childbearing Potential',
    deterministicOnly: true,
    focusNote:
      'Do not generate or paraphrase. Copy verbatim from template_language_fields assembled by the Patient Library decision tree (CBP or NCBP pathway only — never both).',
  },
  contraception_requirements: {
    baseTitle: '5.4.2: Contraception Requirements',
    sectionLabel: '5.4.2 Contraception Requirements',
    deterministicOnly: true,
    focusNote:
      'Do not generate or paraphrase. Copy verbatim Patient Library blocks (F1A/F1B/F2/F3/M1A/M1B) from template_language_fields; append study-specific requirements only after library content.',
  },
  permanent_discontinuation_trial_intervention: {
    baseTitle: '7.1 Permanent discontinuation of Trial Intervention',
    sectionLabel: '7.1 Permanent discontinuation of Trial Intervention',
    focusNote:
      'From intake: replace [X] with what the participant remains evaluated for (e.g. endpoints, safety). Insert liver-stopping algorithm reference per intake/library; pick QTcB or QTcF (or other) to match the protocol. Omit or mark N/A liver/QTc subsections if not applicable to the design.',
  },
  temporary_discontinuation_trial_intervention: {
    baseTitle: '7.1.2 Temporary Discontinuation of Trial Intervention',
    sectionLabel: '7.1.2 Temporary Discontinuation of Trial Intervention',
    focusNote:
      'Author exclusively from protocol source excerpts via the Section 7.1.2 decision panel. Never invent interruption durations, restart criteria, or monitoring not in the source.',
  },
  rechallenge: {
    baseTitle: '7.1.3 Rechallenge',
    sectionLabel: '7.1.3 Rechallenge',
    focusNote:
      'Author exclusively from protocol source excerpts via the Section 7.1.3 decision panel. Never invent restart criteria, approvals, or monitoring. Cross-reference 7.1.3.1 for liver-specific text only.',
  },
  participant_discontinuation_withdrawal: {
    baseTitle: '7.2 Participant Discontinuation or Withdrawal from Trial',
    sectionLabel: '7.2 Participant Discontinuation or Withdrawal from Trial',
  },
  management_loss_to_followup: {
    baseTitle: '7.3 : Management of Loss to Follow-Up',
    sectionLabel: '7.3 Management of Loss to Follow-Up',
  },
  definition_adverse_event: {
    baseTitle: '9.1.1 Definition of Adverse event',
    sectionLabel: '9.1.1 Definition of Adverse event',
  },
  definition_serious_adverse_event: {
    baseTitle: '9.1.2 Definition of Serious Adverse event',
    sectionLabel: '9.1.2 Definition of Serious Adverse event',
  },
  definitions_product_complaints: {
    baseTitle: '9.1.3 Definitions of Product Complaints',
    sectionLabel: '9.1.3 Definitions of Product Complaints',
  },
  ae_reporting_timing_procedures: {
    baseTitle: '9.2 Timing and Procedures for Collection and Reporting',
    sectionLabel: '9.2 Timing and Procedures for Collection and Reporting',
  },
  ae_reporting_timing: {
    baseTitle: '9.2.1 Timing',
    sectionLabel: '9.2.1 Timing',
  },
  ae_reporting_collection_procedure: {
    baseTitle: '9.2.2 Collection procedure',
    sectionLabel: '9.2.2 Collection procedure',
  },
  ae_reporting_reporting: {
    baseTitle: '9.2.3 Reporting',
    sectionLabel: '9.2.3 Reporting',
  },
  ae_reporting_special_interest: {
    baseTitle: '9.2.4 Adverse events of special interest',
    sectionLabel: '9.2.4 Adverse events of special interest',
  },
  ae_reporting_disease_related_events: {
    baseTitle: '9.2.5 Disease-related Events or Outcomes Not Qualifying as AEs or SAEs',
    sectionLabel: '9.2.5 Disease-related Events or Outcomes Not Qualifying as AEs or SAEs',
  },
  pregnancy_postpartum_information: {
    baseTitle: '9.3 Pregnancy and Postpartum Information',
    sectionLabel: '9.3 Pregnancy and Postpartum Information',
  },
  participants_pregnant_during_trial: {
    baseTitle: '9.3.1 Participants Who Become Pregnant During the Trial',
    sectionLabel: '9.3.1 Participants Who Become Pregnant During the Trial',
  },
  special_safety_situations: {
    baseTitle: '9.4 Special Safety situations',
    sectionLabel: '9.4 Special Safety situations',
  },
};
